<template>
  <div
    ref="root"
    class="sidebar"
    :class="{ 'sidebar--collapsed': collapsed, 'sidebar--overlay': overlay }"
    :role="overlay ? 'dialog' : undefined"
    :aria-modal="overlay ? 'true' : undefined"
    :aria-label="overlay ? $t('sidebar.navigation') : undefined"
    @keydown="onKeydown"
  >
    <component
      :is="component"
      v-for="(component, index) in impersonateComponent"
      :key="index"
    ></component>
    <template v-if="showAdmin">
      <PanelHeader class="sidebar__head">
        <button
          type="button"
          class="sidebar__back"
          :aria-label="$t('action.back')"
          @click="setShowAdmin(false)"
        >
          <i class="sidebar__back-icon iconoir-nav-arrow-left"></i>
        </button>
        <h2 v-show="!collapsed" class="panel-header__title">
          {{ $t('sidebar.adminSettings') }}
        </h2>
      </PanelHeader>
      <SidebarAdmin v-show="!collapsed"></SidebarAdmin>
    </template>
    <template v-if="!showAdmin">
      <PanelHeader class="panel-header--stretch">
        <!-- The visible name is `v-show`n off in the rail, and `display: none`
             takes it out of the accessibility tree with it -- which left this
             control an unlabelled avatar. The `aria-label` names the action and
             carries the workspace, so the button is announced in both states,
             and the tooltip gives the rail the name the label row shows when
             expanded. -->
        <button
          ref="workspaceContextAnchor"
          v-tooltip="collapsed ? selectedWorkspace.name || name : null"
          type="button"
          class="sidebar__workspaces-selector"
          data-highlight="workspaces"
          :aria-label="`${$t('sidebar.switchWorkspace')}: ${
            selectedWorkspace.name || name
          }`"
          aria-haspopup="menu"
          @click="
            $refs.workspacesContext.toggle(
              $refs.workspaceContextAnchor,
              'bottom',
              'left',
              8,
              16
            )
          "
        >
          <Avatar
            :initials="
              $filters.nameAbbreviation(selectedWorkspace.name || name)
            "
          ></Avatar>
          <span
            v-show="!collapsed"
            class="sidebar__workspaces-selector-selected-workspace"
            >{{ selectedWorkspace.name || name }}</span
          >
          <span
            v-show="!collapsed"
            v-if="unreadNotificationsInOtherWorkspaces"
            class="sidebar__unread-notifications-icon"
          ></span>
          <i
            v-show="!collapsed"
            class="sidebar__workspaces-selector-icon baserow-icon-up-down-arrows"
          ></i>
        </button>
        <!-- Sits in the header, level with the views rail's toggle, because
             the two panels are adjacent and do the same job: a user who has
             found one has found the other. It used to live in the footer, so
             the two controls for the two halves of the same navigation sat at
             opposite ends of the screen. Hidden in the rail, where the footer
             toggle takes over -- see SidebarFoot. -->
        <button
          v-show="!collapsed && toggleActionable"
          v-tooltip="sidebarToggleLabel"
          type="button"
          class="sidebar__collapse-toggle"
          :aria-label="sidebarToggleLabel"
          :aria-expanded="!collapsed"
          @click="$emit('set-col1-collapsed', !collapsed)"
        >
          <i class="baserow-icon-sidebar" aria-hidden="true"></i>
        </button>
      </PanelHeader>
      <SidebarUserContext
        ref="workspacesContext"
        :workspaces="workspaces"
        :selected-workspace="selectedWorkspace"
        @toggle-admin="setShowAdmin($event)"
      ></SidebarUserContext>

      <!-- Stays in the rail. Home, search, notifications and trash are primary
           navigation, and hiding them made collapsing cost far more than the
           labels: the unread notification count in particular became
           unreachable without expanding first. The rows narrow to their icons
           through `.sidebar--collapsed`, the same way the views rail narrows
           its own. -->
      <SidebarMenu
        v-if="hasSelectedWorkspace"
        :selected-workspace="selectedWorkspace"
        :right-sidebar-open="rightSidebarOpen"
        :collapsed="collapsed"
        @open-workspace-search="$emit('open-workspace-search')"
      ></SidebarMenu>

      <SidebarWithWorkspace
        v-show="!collapsed"
        v-if="hasSelectedWorkspace"
        :applications="applications"
        :selected-workspace="selectedWorkspace"
      ></SidebarWithWorkspace>

      <!-- The rail is not the expanded tree with things hidden: it is a
           narrower presentation of the same applications, so collapsing costs
           the labels rather than the navigation. Before this, collapsing hid
           every section and left 52px of empty column. -->
      <SidebarCollapsedRail
        v-show="collapsed"
        v-if="hasSelectedWorkspace"
        :applications="applications"
        :selected-workspace="selectedWorkspace"
        @selected="selectApplication"
      ></SidebarCollapsedRail>

      <SidebarWithoutWorkspace
        v-show="!collapsed"
        v-if="!hasSelectedWorkspace"
        :workspaces="workspaces"
      ></SidebarWithoutWorkspace>
    </template>
    <!-- In the rail the header has no room for the toggle beside the avatar,
         so the footer carries it there and only there. Exactly one collapse
         control is visible in either state. -->
    <button
      v-show="collapsed && toggleActionable"
      v-tooltip="sidebarToggleLabel"
      type="button"
      class="sidebar__collapse-toggle sidebar__collapse-toggle--rail"
      :aria-label="sidebarToggleLabel"
      :aria-expanded="!collapsed"
      @click="$emit('set-col1-collapsed', !collapsed)"
    >
      <i class="baserow-icon-sidebar" aria-hidden="true"></i>
    </button>
    <SidebarFoot :collapsed="collapsed" :width="width"></SidebarFoot>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { notifyIf } from '@baserow/modules/core/utils/error'
import {
  focusFirstElement,
  handleDialogTabKey,
  restoreFocus,
} from '@baserow/modules/core/utils/dialogFocus'
import SidebarUserContext from '@baserow/modules/core/components/sidebar/SidebarUserContext'
import SidebarWithWorkspace from '@baserow/modules/core/components/sidebar/SidebarWithWorkspace'
import SidebarWithoutWorkspace from '@baserow/modules/core/components/sidebar/SidebarWithoutWorkspace'
import SidebarAdmin from '@baserow/modules/core/components/sidebar/SidebarAdmin'
import SidebarFoot from '@baserow/modules/core/components/sidebar/SidebarFoot'
import SidebarMenu from '@baserow/modules/core/components/sidebar/SidebarMenu'
import SidebarCollapsedRail from '@baserow/modules/core/components/sidebar/SidebarCollapsedRail'
import PanelHeader from '@baserow/modules/core/components/PanelHeader'
import SidebarAdminItem from './SidebarAdminItem.vue'

export default {
  name: 'Sidebar',
  components: {
    SidebarAdmin,
    SidebarWithoutWorkspace,
    SidebarWithWorkspace,
    SidebarUserContext,
    SidebarMenu,
    SidebarCollapsedRail,
    SidebarFoot,
    PanelHeader,
  },
  props: {
    applications: {
      type: Array,
      required: true,
    },
    workspaces: {
      type: Array,
      required: true,
    },
    selectedWorkspace: {
      type: Object,
      required: true,
    },
    collapsed: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    // True below `sm`, where the sidebar floats over the content rather than
    // taking a column. That makes it a modal surface -- `05-sidebar-system.md`
    // §3.3 -- so it gains dialog semantics, a focus trap and Escape.
    overlay: {
      type: Boolean,
      required: false,
      default: false,
    },
    // False in `[sm, md)`, where the viewport forces the rail and the toggle
    // could only lie about what it does. Not rendered rather than disabled: a
    // disabled control still says "this action exists here", and it does not.
    toggleActionable: {
      type: Boolean,
      required: false,
      default: true,
    },
    width: {
      type: Number,
      required: false,
      default: 240,
    },
    rightSidebarOpen: {
      type: Boolean,
      required: false,
      default: false,
    },
    // True when the sidebar is a rail because the views panel holds the left
    // navigation budget (Model C), rather than because the user collapsed it.
    // Only used to explain that state in the expand control's label.
    forcedByViews: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['open-workspace-search', 'set-col1-collapsed', 'dismiss-overlay'],
  data() {
    return {
      showAdmin: false,
      // Where focus was when the overlay opened, so it can be put back.
      returnFocusTo: null,
    }
  },

  computed: {
    SidebarAdminItem() {
      return SidebarAdminItem
    },
    impersonateComponent() {
      return Object.values(this.$registry.getAll('plugin'))
        .map((plugin) => plugin.getImpersonateComponent())
        .filter((component) => component !== null)
    },
    hasSelectedWorkspace() {
      return Object.prototype.hasOwnProperty.call(this.selectedWorkspace, 'id')
    },
    /**
     * Expanding the sidebar while the views panel holds the navigation budget
     * also collapses that panel. The control says so, so the trade is visible
     * before it is made rather than looking like the views panel closed itself.
     */
    expandLabel() {
      return this.forcedByViews
        ? this.$t('sidebar.expandOverViews')
        : this.$t('sidebar.expand')
    },
    sidebarToggleLabel() {
      return this.collapsed ? this.expandLabel : this.$t('sidebar.collapse')
    },
    ...mapGetters({
      name: 'auth/getName',
      unreadNotificationsInOtherWorkspaces:
        'notification/anyOtherWorkspaceWithUnread',
    }),
  },
  watch: {
    /**
     * The overlay is a modal surface, so opening it moves focus in and closing
     * it puts focus back where it came from. Without the first, a keyboard user
     * opening the sidebar is still somewhere in the content behind the scrim;
     * without the second, dismissing it drops focus to the document.
     */
    overlay(isOverlay) {
      if (isOverlay) {
        this.returnFocusTo =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null
        this.$nextTick(() => focusFirstElement(this.$refs.root))
      } else {
        restoreFocus(this.$refs.root, this.returnFocusTo)
        this.returnFocusTo = null
      }
    },
    /**
     * A route change dismisses the overlay. Navigating is the thing the sidebar
     * is *for*, so leaving it open over the page the user just asked for makes
     * every selection take two gestures.
     */
    $route() {
      if (this.overlay) {
        this.$emit('dismiss-overlay')
      }
    },
  },
  created() {
    // Checks whether the rendered page is an admin page. If so, switch the left sidebar
    // navigation to the admin.
    this.showAdmin = Object.values(this.$registry.getAll('admin')).some(
      (adminType) => {
        return this.$route.matched.some(
          ({ name }) => name === adminType.routeName
        )
      }
    )
  },
  methods: {
    /**
     * Escape dismisses, and Tab is confined to the panel.
     *
     * Both only while overlaying: the docked sidebar is part of the page, and
     * trapping focus in it would strand the user in navigation they cannot tab
     * out of -- the failure this is meant to prevent, inverted.
     */
    onKeydown(event) {
      if (!this.overlay) {
        return
      }
      if (event.key === 'Escape') {
        event.stopPropagation()
        this.$emit('dismiss-overlay')
        return
      }
      handleDialogTabKey(event, this.$refs.root)
    },
    setShowAdmin(value) {
      this.showAdmin = value
      this.$forceUpdate()
    },
    /**
     * Selecting from the rail goes through the same store action the expanded
     * tree uses, so an application opened from either presentation ends up in
     * the same state. Each application type's own sidebar component owns the
     * routing that follows from `application/select`.
     *
     * It also expands the sidebar, because selecting a database *reveals its
     * tables* -- and those live in the expanded tree. In the rail they render
     * but measure 0x0, so without this the rail was a dead end: the row went
     * active, the tables loaded, and the user saw nothing they could click.
     * Expanding is what makes the result of the selection visible, so it is
     * part of the selection rather than a separate thing to ask for.
     */
    async selectApplication(application) {
      try {
        await this.$store.dispatch('application/select', application)
        this.$emit('set-col1-collapsed', false)
      } catch (error) {
        notifyIf(error, 'workspace')
      }
    },
  },
}
</script>
